import { addDays, addHours, format } from "date-fns"

export type CalendarProvider = "google" | "outlook" | "ical"

export interface CalendarEventDetails {
  title: string
  description: string
  startDate: Date | string
  endDate?: Date | string
  timezone?: string
  isAllDay: boolean
  location?: string
  url?: string
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value)
}

function getResolvedDates(event: CalendarEventDetails) {
  const start = toDate(event.startDate)
  const rawEnd = event.endDate ? toDate(event.endDate) : undefined

  return {
    start,
    end: event.isAllDay
      ? addDays(rawEnd ?? start, 1)
      : rawEnd ?? addHours(start, 2),
  }
}

function formatUtc(date: Date): string {
  return `${date.toISOString().replace(/[-:]/g, "").split(".")[0]}Z`
}

function formatAllDay(date: Date): string {
  return format(date, "yyyyMMdd")
}

/**
 * Escape special characters in ICS text values.
 * Backslashes, semicolons, commas, and newlines need to be escaped
 * so calendar apps can parse the generated .ics file correctly.
 */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n")
}

/**
 * Fold ICS content lines according to the iCalendar specification.
 * Lines must not exceed 75 characters, with continuation lines
 * beginning with a single space.
 */
function foldIcsLine(line: string): string {
  if (line.length <= 75)
    return line

  const lines = [line.slice(0, 75)]
  let remaining = line.slice(75)

  while (remaining.length > 0) {
    lines.push(` ${remaining.slice(0, 74)}`)
    remaining = remaining.slice(74)
  }

  return lines.join("\r\n")
}

function icsProperty(name: string, value: string): string {
  return foldIcsLine(`${name}:${value}`)
}

function icsPropertyWithParams(name: string, params: string, value: string): string {
  return foldIcsLine(`${name};${params}:${value}`)
}

function buildDescription(event: CalendarEventDetails): string {
  if (!event.url)
    return event.description ?? ""
  if (!event.description)
    return event.url

  return `${event.description}\n\n${event.url}`
}

function openCalendarUrl(url: string) {
  window.open(url, "_blank", "noopener,noreferrer")
}

export function buildIcsContent(event: CalendarEventDetails): string {
  const { start, end } = getResolvedDates(event)
  const description = buildDescription(event)

  const properties: string[] = [
    icsProperty("UID", `${crypto.randomUUID()}@guestpix.com`),
    icsProperty("DTSTAMP", formatUtc(new Date())),
    event.isAllDay
      ? icsPropertyWithParams("DTSTART", "VALUE=DATE", formatAllDay(start))
      : icsProperty("DTSTART", formatUtc(start)),
    event.isAllDay
      ? icsPropertyWithParams("DTEND", "VALUE=DATE", formatAllDay(end))
      : icsProperty("DTEND", formatUtc(end)),
    icsProperty("SUMMARY", escapeIcsText(event.title)),
  ]

  if (description)
    properties.push(icsProperty("DESCRIPTION", escapeIcsText(description)))
  if (event.location)
    properties.push(icsProperty("LOCATION", escapeIcsText(event.location)))
  if (event.url)
    properties.push(icsProperty("URL", event.url))

  properties.push(
    icsProperty("STATUS", "CONFIRMED"),
    icsProperty("TRANSP", "TRANSPARENT"),
  )

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//GuestPix//SaveToCalendar//EN",
    "BEGIN:VEVENT",
    ...properties,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n").concat("\r\n")
}

export function buildGoogleCalendarUrl(event: CalendarEventDetails): string {
  const { start, end } = getResolvedDates(event)

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    details: buildDescription(event),
    location: event.location ?? "",
    dates: event.isAllDay
      ? `${formatAllDay(start)}/${formatAllDay(end)}`
      : `${formatUtc(start)}/${formatUtc(end)}`,
  })

  if (!event.isAllDay && event.timezone) {
    params.set("ctz", event.timezone)
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

export function buildOutlookCalendarUrl(event: CalendarEventDetails): string {
  const { start, end } = getResolvedDates(event)

  const params = new URLSearchParams({
    subject: event.title,
    body: buildDescription(event),
    location: event.location ?? "",
    startdt: start.toISOString(),
    enddt: end.toISOString(),
  })

  return `https://outlook.office.com/calendar/deeplink/compose?${params.toString()}`
}

export function downloadIcsCalendar(event: CalendarEventDetails): boolean {
  const content = buildIcsContent(event)
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" })
  const objectUrl = URL.createObjectURL(blob)

  const anchor = document.createElement("a")
  anchor.href = objectUrl
  anchor.download = "event.ics"

  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)

  URL.revokeObjectURL(objectUrl)

  return true
}

export function saveToCalendar(
  provider: CalendarProvider,
  event: CalendarEventDetails,
): boolean {
  if (provider === "google") {
    openCalendarUrl(buildGoogleCalendarUrl(event))
    return true
  }

  if (provider === "outlook") {
    openCalendarUrl(buildOutlookCalendarUrl(event))
    return true
  }

  return downloadIcsCalendar(event)
}
